/**
 * PPM Grade Thresholds Section - CR-6652
 *
 * Fully configurable colour grading for PPM, presented with the same
 * card-list / click-to-edit interaction as the OTIF Grade Thresholds
 * section. Grade id, label, colour and description mirror that section;
 * the PPM value band is PPM's own.
 */
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Loader2, RotateCcw, Pencil, X, Check, Plus, Trash2, Gauge } from 'lucide-react';
import { PPMGradeThreshold, CreatePPMGradeThresholdData } from '../../../lib/api';
import { PPMGradeEditData } from './usePPMGradeThresholds';
import { bandRangeText, byPpmBest, ppmLimits } from '../../../lib/gradeBandRange';

interface PPMGradeThresholdsSectionProps {
  bands: PPMGradeThreshold[];
  editingBand: number | null;
  bandEditData: PPMGradeEditData | null;
  savingBand: boolean;
  showAddBand: boolean;
  newBandData: CreatePPMGradeThresholdData;
  addingBand: boolean;
  resettingBands: boolean;
  qualityLabel: string;
  onSetShowAddBand: (show: boolean) => void;
  onResetBands: () => void;
  onEditBand: (band: PPMGradeThreshold) => void;
  onCancelEditBand: () => void;
  onSaveBand: (bandId: number) => void;
  onBandFieldChange: (field: string, value: string | number) => void;
  onAddBand: () => void;
  onDeleteBand: (bandId: number, label: string) => void;
  onNewBandFieldChange: (field: string, value: string | number) => void;
}

export function PPMGradeThresholdsSection({
  bands,
  editingBand,
  bandEditData,
  savingBand,
  showAddBand,
  newBandData,
  addingBand,
  resettingBands,
  qualityLabel,
  onSetShowAddBand,
  onResetBands,
  onEditBand,
  onCancelEditBand,
  onSaveBand,
  onBandFieldChange,
  onAddBand,
  onDeleteBand,
  onNewBandFieldChange,
}: PPMGradeThresholdsSectionProps) {
  // CR-8677: best grade (lowest PPM) first, and each range stops short of the next one.
  const sorted = [...bands].sort(byPpmBest);
  const limits = bands.map(ppmLimits);

  return (
    <div className="space-y-4 border-t pt-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            {qualityLabel} Grade Thresholds
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Define the {qualityLabel} value bands, labels and colours used for quality colour coding on
            dashboards. Click a grade to edit it. These are independent of OTIF Grade Thresholds.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => onSetShowAddBand(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Grade
          </Button>
          <Button variant="outline" size="sm" onClick={onResetBands} disabled={resettingBands}>
            {resettingBands ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Reset to Defaults
          </Button>
        </div>
      </div>

      {sorted.length > 0 ? (
        <div className="space-y-4">
          {sorted.map((band) => (
            <div key={band.id}>
              {editingBand === band.id && bandEditData ? (
                <PPMGradeEditForm
                  band={band}
                  bandEditData={bandEditData}
                  savingBand={savingBand}
                  qualityLabel={qualityLabel}
                  onCancelEditBand={onCancelEditBand}
                  onSaveBand={onSaveBand}
                  onBandFieldChange={onBandFieldChange}
                  onDeleteBand={onDeleteBand}
                />
              ) : (
                <PPMGradeViewItem
                  band={band}
                  rangeText={bandRangeText(ppmLimits(band), limits)}
                  qualityLabel={qualityLabel}
                  onEditBand={onEditBand}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-center py-4">
          No {qualityLabel} grades configured. Click "Reset to Defaults" to create the default grades.
        </p>
      )}

      {showAddBand && (
        <AddPPMGradeForm
          newBandData={newBandData}
          addingBand={addingBand}
          qualityLabel={qualityLabel}
          onSetShowAddBand={onSetShowAddBand}
          onAddBand={onAddBand}
          onNewBandFieldChange={onNewBandFieldChange}
        />
      )}
    </div>
  );
}

interface PPMGradeEditFormProps {
  band: PPMGradeThreshold;
  bandEditData: PPMGradeEditData;
  savingBand: boolean;
  qualityLabel: string;
  onCancelEditBand: () => void;
  onSaveBand: (bandId: number) => void;
  onBandFieldChange: (field: string, value: string | number) => void;
  onDeleteBand: (bandId: number, label: string) => void;
}

function PPMGradeEditForm({
  band,
  bandEditData,
  savingBand,
  qualityLabel,
  onCancelEditBand,
  onSaveBand,
  onBandFieldChange,
  onDeleteBand,
}: PPMGradeEditFormProps) {
  return (
    <div
      className="p-4 border rounded-lg space-y-4"
      style={{ borderLeftColor: bandEditData.color, borderLeftWidth: '4px' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: bandEditData.color }}
          >
            {bandEditData.grade || '?'}
          </div>
          <div className="font-medium text-lg">
            Edit {qualityLabel} Grade {bandEditData.label || bandEditData.grade}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelEditBand} disabled={savingBand}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button size="sm" onClick={() => onSaveBand(band.id)} disabled={savingBand}>
            {savingBand ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2">
          <Label htmlFor={`ppm-grade-${band.id}`}>Grade ID</Label>
          <Input
            id={`ppm-grade-${band.id}`}
            value={bandEditData.grade}
            onChange={(e) => onBandFieldChange('grade', e.target.value)}
            placeholder="e.g., A, B, C"
            maxLength={10}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`ppm-label-${band.id}`}>Display Label</Label>
          <Input
            id={`ppm-label-${band.id}`}
            value={bandEditData.label}
            onChange={(e) => onBandFieldChange('label', e.target.value)}
            placeholder="e.g., Platinum, Gold"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`ppm-min-${band.id}`}>Min {qualityLabel}</Label>
          <Input
            id={`ppm-min-${band.id}`}
            type="number"
            step="1"
            min="0"
            max="1000000"
            value={bandEditData.min_ppm}
            onChange={(e) => onBandFieldChange('min_ppm', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`ppm-max-${band.id}`}>Max {qualityLabel}</Label>
          <Input
            id={`ppm-max-${band.id}`}
            type="number"
            step="0.01"
            min="0"
            max="1000000"
            value={bandEditData.max_ppm}
            onChange={(e) => onBandFieldChange('max_ppm', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`ppm-color-${band.id}`}>Color</Label>
          <div className="flex items-center gap-2">
            <input
              id={`ppm-color-${band.id}`}
              type="color"
              value={bandEditData.color}
              onChange={(e) => onBandFieldChange('color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border"
            />
            <Input
              value={bandEditData.color}
              onChange={(e) => onBandFieldChange('color', e.target.value)}
              placeholder="#22C55E"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 space-y-2">
          <Label htmlFor={`ppm-description-${band.id}`}>Description</Label>
          <Input
            id={`ppm-description-${band.id}`}
            value={bandEditData.description}
            onChange={(e) => onBandFieldChange('description', e.target.value)}
            placeholder={`Description for this ${qualityLabel} grade`}
          />
        </div>
        <div className="pt-6">
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteBand(band.id, band.display_label || band.label || band.grade);
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

interface PPMGradeViewItemProps {
  band: PPMGradeThreshold;
  /** The band's range as listed, e.g. "500.00 - 1999.99". */
  rangeText: string;
  qualityLabel: string;
  onEditBand: (band: PPMGradeThreshold) => void;
}

function PPMGradeViewItem({ band, rangeText, qualityLabel, onEditBand }: PPMGradeViewItemProps) {
  const label = band.display_label || band.label || `Grade ${band.grade}`;

  return (
    <div
      className="flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
      style={{ borderLeftColor: band.color, borderLeftWidth: '4px' }}
      onClick={() => onEditBand(band)}
    >
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
        style={{ backgroundColor: band.color }}
      >
        {band.grade}
      </div>
      <div className="flex-1">
        <div className="font-medium flex items-center gap-2">
          {label}
          {band.label && band.label !== band.grade && (
            <Badge variant="secondary" className="text-xs">
              Grade {band.grade}
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">{band.description}</div>
      </div>
      <div className="text-right">
        <div className="font-medium">
          {rangeText}
        </div>
        <div className="text-sm text-muted-foreground">{qualityLabel} Range</div>
      </div>
      <Pencil className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

interface AddPPMGradeFormProps {
  newBandData: CreatePPMGradeThresholdData;
  addingBand: boolean;
  qualityLabel: string;
  onSetShowAddBand: (show: boolean) => void;
  onAddBand: () => void;
  onNewBandFieldChange: (field: string, value: string | number) => void;
}

function AddPPMGradeForm({
  newBandData,
  addingBand,
  qualityLabel,
  onSetShowAddBand,
  onAddBand,
  onNewBandFieldChange,
}: AddPPMGradeFormProps) {
  return (
    <div className="mt-4 p-4 border rounded-lg space-y-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: newBandData.color }}
          >
            {newBandData.grade || '?'}
          </div>
          <div className="font-medium text-lg">Add New {qualityLabel} Grade</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSetShowAddBand(false)}
            disabled={addingBand}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button size="sm" onClick={onAddBand} disabled={addingBand || !newBandData.grade.trim()}>
            {addingBand ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Add Grade
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2">
          <Label htmlFor="new-ppm-grade">Grade ID *</Label>
          <Input
            id="new-ppm-grade"
            value={newBandData.grade}
            onChange={(e) => onNewBandFieldChange('grade', e.target.value)}
            placeholder="e.g., A+, B+, S"
            maxLength={10}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-ppm-label">Display Label</Label>
          <Input
            id="new-ppm-label"
            value={newBandData.label}
            onChange={(e) => onNewBandFieldChange('label', e.target.value)}
            placeholder="e.g., Platinum, Elite"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-ppm-min">Min {qualityLabel}</Label>
          <Input
            id="new-ppm-min"
            type="number"
            step="1"
            min="0"
            max="1000000"
            value={newBandData.min_ppm}
            onChange={(e) => onNewBandFieldChange('min_ppm', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-ppm-max">Max {qualityLabel}</Label>
          <Input
            id="new-ppm-max"
            type="number"
            step="0.01"
            min="0"
            max="1000000"
            value={newBandData.max_ppm}
            onChange={(e) => onNewBandFieldChange('max_ppm', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-ppm-color">Color</Label>
          <div className="flex items-center gap-2">
            <input
              id="new-ppm-color"
              type="color"
              value={newBandData.color}
              onChange={(e) => onNewBandFieldChange('color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border"
            />
            <Input
              value={newBandData.color}
              onChange={(e) => onNewBandFieldChange('color', e.target.value)}
              placeholder="#3B82F6"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="new-ppm-description">Description</Label>
        <Input
          id="new-ppm-description"
          value={newBandData.description}
          onChange={(e) => onNewBandFieldChange('description', e.target.value)}
          placeholder={`Description for this ${qualityLabel} grade`}
        />
      </div>
    </div>
  );
}
